import { useState, useEffect } from 'react';
import api from '../../services/api';
import {
  clearTrackingScripts,
  loadGoogleAnalytics,
  loadMarketingScripts,
} from '../../utils/trackingScripts';
import {
  CONSENT_DURATION_DAYS,
  CONSENT_POLICY_VERSION,
  getStoredConsent,
  setStoredConsent,
} from '../../utils/cookieConsent';
import './CookieConsent.css';

const POLICY_TEXT = 'We use cookies to enhance your experience. Strictly necessary cookies are required for the site to function. Analytics cookies help us understand how you use our site. Marketing cookies are used for advertising purposes.';

export default function CookieConsentBanner() {
  const [show, setShow] = useState(() => !getStoredConsent());
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const consent = getStoredConsent();
    if (consent) {
      setPrefs({ analytics: consent.analytics, marketing: consent.marketing });
      loadScripts(consent);
      setShow(false);
      return;
    }
    clearTrackingScripts();
    setShow(true);
  }, []);

  async function persistPreferences(preferences) {
    setLoading(true);
    try {
      const res = await api.post('/cookie-consent', {
        analytics: preferences.analytics,
        marketing: preferences.marketing,
        policyVersion: CONSENT_POLICY_VERSION,
      });
      const consentData = res.data.data?.consent || {
        version: CONSENT_POLICY_VERSION,
        strictly_necessary: true,
        analytics: preferences.analytics,
        marketing: preferences.marketing,
        timestamp: new Date().toISOString(),
        expires: new Date(Date.now() + CONSENT_DURATION_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      };

      clearTrackingScripts();
      const storedConsent = setStoredConsent(consentData);
      loadScripts(storedConsent);
      setPrefs(preferences);
      setShow(false);
      setCustomize(false);
    } catch (error) {
      console.error('Error saving consent:', error);
      alert('Failed to save consent. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function acceptAll() {
    return persistPreferences({ analytics: true, marketing: true });
  }

  function rejectAll() {
    return persistPreferences({ analytics: false, marketing: false });
  }

  function saveCustom() {
    return persistPreferences(prefs);
  }

  function loadScripts(consent) {
    if (consent.analytics) {
      loadGoogleAnalytics();
    }
    if (consent.marketing) {
      loadMarketingScripts();
    }
  }

  if (!show) return null;

  return (
    <div className="cookie-overlay">
      <div className="cookie-banner">
        {!customize ? (
          <>
            <h2>Cookie Preferences</h2>
            <p>
              {POLICY_TEXT}
            </p>
            <p>
              By clicking "Accept All", you consent to our use of cookies. 
              Read our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> for more information.
            </p>
            <div className="cookie-buttons">
              <button 
                onClick={acceptAll} 
                className="btn-accept"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Accept All'}
              </button>
              <button 
                onClick={rejectAll} 
                className="btn-reject"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Reject All'}
              </button>
              <button 
                onClick={() => setCustomize(true)} 
                className="btn-customize"
                disabled={loading}
              >
                Customize
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Customize Cookie Preferences</h2>
            
            <div className="cookie-category">
              <label>
                <input type="checkbox" checked disabled />
                <strong>Strictly Necessary</strong>
              </label>
              <p>Required for the website to function. Cannot be disabled.</p>
            </div>

            <div className="cookie-category">
              <label>
                <input 
                  type="checkbox" 
                  checked={prefs.analytics}
                  onChange={e => setPrefs({...prefs, analytics: e.target.checked})}
                />
                <strong>Analytics</strong>
              </label>
              <p>Help us understand how visitors interact with our website.</p>
            </div>

            <div className="cookie-category">
              <label>
                <input 
                  type="checkbox" 
                  checked={prefs.marketing}
                  onChange={e => setPrefs({...prefs, marketing: e.target.checked})}
                />
                <strong>Marketing/Remarketing</strong>
              </label>
              <p>Used to deliver personalized advertisements.</p>
            </div>

            <div className="cookie-buttons">
              <button 
                onClick={saveCustom} 
                className="btn-save"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Preferences'}
              </button>
              <button 
                onClick={() => setCustomize(false)} 
                className="btn-back"
                disabled={loading}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
